<template>
  <div id="app">
    <BHeader :service="translatedLabel('brandingName', 'shared_Header')"
             :logout-label="translatedLabel('logoutLabel', 'shared_Header')"
             :current-region-label="translatedLabel('currentRegion', 'shared_Regions')"
             :menu-items="menuItems"
             :user="user"
             :regions="regions"
             @logout="logout"
             @change-region="saveRegion"
    />
    <main id="main" class="flex-grow-1 flex-shrink-0">
      <router-view :support="getRegionalizedFeatures.supportApp" :usb="getRegionalizedFeatures.usbOrderForm" :usb-error="usbError" @authenticated="getUser" />
      <div v-if="loading" class="bc-spinner" />
    </main>
    <region-selector-modal v-if="showRegionModal" :regions="regions" @select="saveRegion" />
    <BFooter :disclaimer="translatedLabel('disclaimer', 'shared_Footer')"
             :privacy-policy="getRegion === 'EMEA' ? translatedLabel('privacyPolicyTitle', 'shared_Footer') : ''"
             :privacy-policy-content="translatedLabel('privacyPolicyContent', 'shared_Footer')"
             :languages="languages"
             :selected-language="getLanguage"
             @languagePicked="saveLanguage"
    />
  </div>
</template>

<script>
import BHeader from '@dxp-common-components/header'
import BFooter from '@dxp-common-components/footer'
import RegionSelectorModal from '@/components/ui/RegionSelectorModal'
import appConfig from '@/config'
import axios from '@/axios'
import api from '@/api'
import {dpmmService} from '@/services'
import {mapGetters, mapMutations} from 'vuex'
import {updateStore} from './store'

const initMenuElements = ['serviceLibrary']

export default {
  conceptName: ['shared_HeaderMenu', 'shared_RegionNames', 'shared_LanguagesISO'],
  components: {
    BHeader,
    BFooter,
    RegionSelectorModal
  },
  data() {
    return {
      loading: false,
      error: null,
      user: {},
      menuElements: initMenuElements,
      usbError: false,
      availableRegions: [],
      showRegionModal: false
    }
  },
  computed: {
    ...mapGetters({
      getAppReady: 'servicelibrary/getAppReady',
      isSetRegion: 'servicelibrary/isSetRegion',
      getRegion: 'servicelibrary/getRegion',
      getRegionalizedFeatures: 'servicelibrary/getRegionalizedFeatures',
      getFeatures: 'servicelibrary/getFeatures',
      getLanguage: 'servicelibrary/getLanguage',
      getIdentity: 'servicelibrary/getIdentity',
      getAvailableLanguages: 'servicelibrary/getAvailableLanguages'
    }),
    regions() {
      const regions = this.availableRegions
      return regions.map(conceptKey => ({
        key: conceptKey,
        translation: this.translatedLabel(conceptKey, 'shared_RegionNames'),
        active: this.getRegion === conceptKey
      }))
    },
    menuItems() {
      let items = []
      for(const i of this.menuElements) {
        let iconConcept = i + 'Icon'
        let titleConcept = i + 'Title'
        let pathConcept = i + 'Path'
        if (i === 'troubleshooting') {
          const trobuleshootingItems = []
          let itemObject = {
            'icon': this.$options.filters.translate(iconConcept, this.labels, 'shared_HeaderMenu'),
            'title': this.$options.filters.translate(titleConcept, this.labels, 'shared_HeaderMenu')
          }
          for (let y = 0; y <= 1; y++) {
            let iconConcept = i + 'Icon' + y
            let titleConcept = i + 'Title' + y
            let pathConcept = i + 'Path' + y
            trobuleshootingItems.push({
              'icon': this.$options.filters.translate(iconConcept, this.labels, 'shared_HeaderMenu'),
              'title': this.$options.filters.translate(titleConcept, this.labels, 'shared_HeaderMenu'),
              'path': `${this.$options.filters.translate(pathConcept, this.labels, 'shared_HeaderMenu')}?area=${this.getRegion}`
            })
          }
          itemObject.items = trobuleshootingItems
          items.push(itemObject)
        } else {
          items.push(
            {
              'icon': this.$options.filters.translate(iconConcept, this.labels, 'shared_HeaderMenu'),
              'title': this.$options.filters.translate(titleConcept, this.labels, 'shared_HeaderMenu'),
              'path': this.$options.filters.translate(pathConcept, this.labels, 'shared_HeaderMenu')
            }
          )
        }
      }
      return items
    },
    languages() {
      const ret = []
      const languageNames = this.labels['shared_LanguagesISO'] || {}
      let languageList = this.getAvailableLanguages
      if (languageList) {
        languageList.sort()
        if (languageList.indexOf('en') >= 0) {
          languageList = languageList.filter(item => item !== 'en')
          languageList.unshift('en')
        }
        for (const lang of languageList) {
          if (languageNames && languageNames[lang]) ret.push({value: lang, text: languageNames[lang]})
        }
      }
      return ret
    }
  },
  watch: {
    $route(to) {
      this.loading = to.name === null // show/hide loader if route name is null
      this.checkFeatureFlag()
    },
    getRegionalizedFeatures(flags) {
      let menuElements = initMenuElements.slice()
      if (flags.mymachineApp) menuElements.unshift('myMachine')
      if (flags.softwareApp) menuElements.push('software')
      if (flags.videosApp) menuElements.push('videos')
      if (flags.troubleshootingAdmin) menuElements.push('troubleshooting')
      if (flags.supportApp) menuElements.push('support')
      this.menuElements = menuElements
      if (Object.keys(flags).length > 0 && this.isSetRegion) this.checkFeatureFlag()
    },
    async getAppReady() {
      await this.loadFeatureFlags()
      this.getRegions()
    },
    getRegion(newRegion, oldRegion) {
      if (newRegion && Object.keys(this.getRegionalizedFeatures) > 0) this.checkFeatureFlag()
      if (newRegion && !this.getLanguage && this.getIdentity) this.detectLanguage()
      this.$store.dispatch('servicelibrary/fetchRegionConcepts', newRegion)

      document.body.classList.remove(oldRegion)
      document.body.classList.add(newRegion)
    },
    getIdentity(identity) {
      if (identity && !this.getLanguage && this.getRegion) this.detectLanguage()
    }
  },
  async created() {
    this.loading = this.$route.name === null // show/hide loader if route name is null
    this.getUser() // try to get user
    if (this.getAppReady) {
      await this.loadFeatureFlags()
      this.getRegions()
    }

    switch (appConfig.nodeEnv) { //add prefix on dev environments
    case 'development':
      if (process.env.VUE_APP_PREFIX)
        document.title = process.env.VUE_APP_PREFIX + ' Service Library'
      else
        document.title = '[DEV] Service Library'
      break
    case 'int':
      document.title = '[INT] Service Library'
      break
    case 'qa':
      document.title = '[QA] Service Library'
      break
    }
  },
  methods: {
    ...mapMutations({
      saveRegion: 'servicelibrary/saveRegion',
      saveFeatures: 'servicelibrary/saveFeatures',
      saveLanguage: 'servicelibrary/saveLanguage'
    }),
    detectLanguage() {
      let lang = 'en' //default fallback language
      const navigatorLanguages = (navigator.languages && navigator.languages.length) ? navigator.languages : [navigator.userLanguage] || [navigator.language] || [navigator.browserLanguage] || ['en']
      for (let navigatorLanguage of navigatorLanguages) {
        navigatorLanguage = navigatorLanguage.substr(0, 2)
        if (navigatorLanguage && this.languages?.indexOf(navigatorLanguage) !== -1) {
          lang = navigatorLanguage
          break
        }
      }
      const identityLanguage = this.getIdentity.language?.toLowerCase()
      if (identityLanguage && this.languages.indexOf(identityLanguage) !== -1) lang = identityLanguage
      this.$store.commit('servicelibrary/saveLanguage', lang)
    },
    checkFeatureFlag() {
      if (this.getRegion && !this.getRegionalizedFeatures.libraryApp) {
        updateStore(this.$store.state)
        document.location.href = '/service'
      }
      if (this.$route.path === '/usb' && !this.getRegionalizedFeatures.usbOrderForm) {
        this.usbError = true
        setTimeout(() => {this.$router.push('/')}, 10 * 1000)
      }
    },
    async getUser() {
      try {
        this.user = await this.$auth.getUser()
        this.$store.commit('servicelibrary/saveUser', this.user)
        this.$store.dispatch('servicelibrary/fetchIdentity')
        this.$store.dispatch('servicelibrary/fetchRegionConcepts', 'NA')
      } catch (e) {
        await this.$auth.signInWithRedirect()
        this.error = e
      }
    },
    async loadFeatureFlags() {
      await axios.get(api.endpoints.DPMM_API.FEATURES).then((r) => {
        const features = r.data.filter(f => f.enabled).reduce((obj, f) => {
          obj[f.name] = true
          return obj
        }, {})
        this.saveFeatures(features)
        this.checkFeatureFlag()
      }).catch(() => {
        this.$router.push({name: 'Error', query: {'redirect': false, 'error': 'featureUnauthorizedNoRedirect'}}) //on error from DP.API app is not allowed
      })
    },
    getRegions() {
      dpmmService.fetchAvailableRegions().then(r => {
        if (r.data && r.data.business_units) this.availableRegions = r.data.business_units

        if (this.availableRegions.length === 0) {
          //this.$router.push({name: 'Error', query: {'error': 'noRegionsError'}})
        } else if (this.availableRegions.length === 1) {
          this.saveRegion(this.availableRegions[0])
        } else {
          if (!this.isSetRegion) this.showRegionModal = true
        }
      }).catch(() => {
        //this.$router.push({name: 'Error', query: {'error': 'regionsError'}})
      })
    },
    async logout() {
      if (this.$auth) await this.$auth.signOut()
    }
  }
}
</script>

<style lang="scss">
@import './scss/grid';

#main {
  padding-top: 4rem;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
/* pulse insight show only in NA region */
#pulse_feedback_tab {
  display: none;
}

body.NA #pulse_feedback_tab {
  display: block;
}

#onetrust-consent-sdk {
  display: none;
}
.EMEA #onetrust-consent-sdk{
  display: block;
}
</style>
